import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from "@mui/material";

import {
  Field,
  ListFieldsRequest,
} from "@safetyculture/s12-apis-connect-web/s12/users/userfield/v1/field_pb";

import { listFields } from "../../../api/endpoints/user-field";
import { callOptionsHeaders } from "../../../api/helpers";
import { GrpcError, SnackbarContext } from "../../../context/snackbar-context";
import GenericDialog from "../../shared/generic-dialog";
import EmptyTableResults from "../../shared/table/empty-table-results";
import { UserFieldsLoader } from "./user-fields-loader";
import { UserFieldsTableHeader } from "./user-fields-table-header";
import { UserFieldsTableRow } from "./user-fields-table-row";

type UserFieldsDialogProps = {
  open: boolean;
  setIsOpen: (isOpen: boolean) => void;
  roleId: string;
  regionCode: string;
};

export const UserFieldsDialog = (props: UserFieldsDialogProps) => {
  const { open, setIsOpen, roleId, regionCode } = props;
  const { showError } = useContext(SnackbarContext);
  const [fields, setFields] = useState<Field[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const fetchUserFields = useCallback(async () => {
    try {
      setIsLoading(true);
      const req = new ListFieldsRequest({
        orgId: roleId,
      });
      const fields = await listFields(
        req,
        callOptionsHeaders(regionCode, roleId)
      );
      if (fields) {
        setFields(fields);
      }
    } catch (e) {
      showError(e as GrpcError);
    } finally {
      setIsLoading(false);
    }
  }, [regionCode, roleId, showError]);

  useEffect(() => {
    if (open) {
      fetchUserFields();
    }
  }, [fetchUserFields, open]);
  return (
    <GenericDialog
      title="User Fields"
      width="md"
      hasAckBtn={true}
      acknowledgeBtnLabel="OK"
      open={open}
      handleAckBtnClick={() => {
        setIsOpen(false);
      }}
      handleDialogClose={() => {
        setIsOpen(false);
      }}
      data-testid="user-fields-dialog"
    >
      {isLoading && <UserFieldsLoader />}
      {!isLoading && (
        <TableContainer
          style={{ maxHeight: "600px", overflow: "auto" }}
          component={Paper}
        >
          <Table>
            <TableHead>
              <UserFieldsTableHeader />
            </TableHead>
            <TableBody>
              {fields.length === 0 && (
                <EmptyTableResults
                  data-testid="user-field-table-empty"
                  colSpan={5}
                  tableHeading="user fields in this organisation"
                />
              )}
              {fields.map((field) => (
                <UserFieldsTableRow field={field} key={field.id} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </GenericDialog>
  );
};
